// React / Next
import { Link } from '@tanstack/react-router';

// Components
import {
  PortableText,
  type PortableTextComponents,
  type PortableTextBlock,
} from '@portabletext/react';
import { SanityTable } from '../SanityTable/SanityTable';

// Types
import type { SanityTable as SanityTableType } from '../../lib/sanity/types';

type LegalPortableTextProps = {
  value: (PortableTextBlock | SanityTableType)[];
  className?: string;
};

const components: PortableTextComponents = {
  types: {
    table: ({ value }) => <SanityTable value={value} />,
  },
  block: {
    normal: ({ children }) => <p>{children}</p>,
    h3: ({ children }) => (
      <h3 className="font-heading text-[24px] leading-[32px] tracking-[-0.3px] font-light mt-6">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="font-heading text-[20px] leading-[28px] tracking-[-0.2px] font-light mt-4">
        {children}
      </h4>
    ),
  },
  list: {
    bullet: ({ children }) => <ul>{children}</ul>,
    number: ({ children }) => <ol>{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li>{children}</li>,
    number: ({ children }) => <li>{children}</li>,
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    underline: ({ children }) => <u>{children}</u>,
    link: ({ children, value }) => {
      const href = value?.href || '';
      const isExternal = href.startsWith('http');
      const openInNewTab = value?.openInNewTab || isExternal;

      if (isExternal) {
        return (
          <a
            href={href}
            className="underline underline-offset-2 hover:text-orange-800 transition-colors"
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
          >
            {children}
          </a>
        );
      }

      return (
        <Link
          to={href}
          className="underline underline-offset-2 hover:text-orange-800 transition-colors"
        >
          {children}
        </Link>
      );
    },
    email: ({ children, value }) => {
      const emailAddress = value?.emailAddress || '';
      return (
        <a
          href={`mailto:${emailAddress}`}
          className="underline underline-offset-2 hover:text-orange-800 transition-colors"
        >
          {children}
        </a>
      );
    },
  },
};

export const LegalPortableText = ({ value, className = '' }: LegalPortableTextProps) => {
  if (!value || value.length === 0) return null;

  return (
    <div className={className}>
      <PortableText value={value as PortableTextBlock[]} components={components} />
    </div>
  );
};


